// src/services/subscriptionApi.js
import { supabase } from '../config';

/**
 * Makes an authenticated API request
 */
const makeAuthenticatedRequest = async (url, options = {}) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText || `HTTP ${response.status}` };
      }
      throw new Error(errorData.message || `Request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

/**
 * Calls the backend to create or update a product subscription for a user.
 */
export const subscribeToProduct = async (userId, productName, role) => {
  try {
    const response = await makeAuthenticatedRequest('/api/subscription_api', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        product_name: productName,
        role: role,
      }),
    });
    return response;
  } catch (error) {
    console.error('Failed to subscribe to product:', error.message);
    throw error;
  }
};

/**
 * Fetches the product roles for the currently authenticated user.
 */
export const fetchUserRoles = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_product_roles')
      .select('*')
      .eq('user_id', user.id);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user roles:', error.message);
    throw error;
  }
};

/**
 * Checks if user has a specific product role
 */
export const hasProductRole = async (userId, productName, requiredRole = null) => {
  try {
    let query = supabase
      .from('user_product_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('product_name', productName);

    if (requiredRole) {
      query = query.eq('role', requiredRole);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data && data.length > 0;
  } catch (error) {
    console.error('Error checking product role:', error);
    return false;
  }
};

/**
 * Updates user's role for a specific product
 */
export const updateProductRole = async (userId, productName, newRole) => {
  try {
    const response = await makeAuthenticatedRequest('/api/subscription_api', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        product_name: productName,
        role: newRole,
      }),
    });
    return response;
  } catch (error) {
    console.error('Failed to update product role:', error.message);
    throw error;
  }
};